import { computed, observable, reaction, toJS, makeObservable } from 'mobx'
import _ from 'lodash'
import axios from 'axios'
import constants, { selectionTypes, defaultPolygonColor } from 'constants.js'
import { lineString } from '@turf/helpers'
import area from '@turf/area'
import length from '@turf/length'
import api from 'api.js'
import Wkt from 'wicket/wicket'
import Position from './common/Position'
import { catchError } from './ErrorStore'
import { distanceBetweenPoints, turfPointToLatLng, getDefaultGrayColor } from '../utils'
import { selectionStore, dataStore } from './UIStore'
import { shapeTypes } from '../constants'
import i18next from '../i18n'

const REMOVING_DISTANCE = 9

// Represents a buffer around a line, while the user is editing it.
// (After adding to a target group, the result will be a Geopolygon)
class Line {
  @observable name = ''
  @observable points = []
  @observable drawing = true
  @observable wasDrawing = false
  @observable removing = false
  @observable invalid = observable.map()
  @observable buffer = 10
  @observable bufferUnit = 'km'
  @observable keys = {}
  @observable position = new Position()
  @observable.ref region = null
  @observable updatingRegion = false
  @observable color_id = null
  @observable transparency = 50
  updateBufferCancel = axios.CancelToken.source()

  constructor (isUpdating, buffer, points, name, id, targetGroupId, bufferUnit) {
    makeObservable(this)
    if (isUpdating) {
      this.isUpdating = true
      this.drawing = false
      this.wasDrawing = true
      this.buffer = buffer
      this.bufferUnit = bufferUnit === 'mile' ? 'mile' : 'km'
      points.forEach((point) => this.points.push(point))
      this.name = name
      this.id = id
      this.targetGroupId = targetGroupId
      this.updateBuffer()
    }
    // when this data changes, the buffered region needs
    // to be recalculated
    const regionData = () => [toJS(this.points), this.buffer, this.bufferUnit]

    // immediately mark the region as updating
    // and cancel any pending update request
    reaction(regionData, (a) => {
      this.updateBufferCancel.cancel()
      this.updateBufferCancel = axios.CancelToken.source()
      this.updatingRegion = true
    })

    // asynchronously request the updated region
    reaction(regionData, () => {
      this.updateBuffer()
    }, { delay: 250 })
  }

  @catchError
  async updateBuffer () {
    try {
      if (this.points.length > 1 && this.buffer) {
        const response = await api.postBuffer({
          the_geom: JSON.stringify(this.drawnLine),
          buffer: this.bufferMeters
        }, { cancelToken: this.updateBufferCancel.token })

        const wkt = new Wkt.Wkt()
        this.region = wkt.read(response.data.polygon).toJson()
        this.updatingRegion = false
      } else {
        this.region = null
        this.updatingRegion = false
      }
    } catch (e) {
      if (!axios.isCancel(e)) {
        throw e
      }
    }
  }

  // the point under the user's cursor which is shown
  // as a translucent final point in the line
  @computed get finalPoint () {
    return this.position.geoJSON
  }

  @computed get lastTwoPointsDistance () {
    return distanceBetweenPoints(this.points, this.finalPoint)
  }

  // the line, not including the translucent final segment
  // which connects to the users mouse cursor
  @computed get drawnLine () {
    if (this.points.length > 1) {
      return lineString(toJS(this.points)).geometry
    } else {
      return null
    }
  }

  // the line including the final segment
  @computed get finalLine () {
    if (this.drawing && this.points.length >= 1) {
      return lineString([
        this.points[this.points.length - 1],
        this.position.geoJSON
      ]).geometry
    } else {
      return null
    }
  }

  @computed get drawingAreaSquareKilometers () {
    if (this.region !== null) {
      return area(this.region) / 1000000
    } else {
      return 0
    }
  }

  @computed get bufferMeters () {
    const kilometers = this.bufferUnit === 'mile' ? this.buffer * 1.609344 : this.buffer
    return kilometers * 1000
  }

  @computed get drawnLineLengthKilometers () {
    if (this.drawnLine === null) {
      return 0
    } else {
      return length(this.drawnLine)
    }
  }

  @computed get largeEnoughToBeInaccurate () {
    return this.drawingAreaSquareKilometers > constants.INACCURATE_BUFFER_AREA ||
      this.drawnLineLengthKilometers > constants.INACCURATE_BUFFER_LENGTH
  }

  @computed get tooLarge () {
    return this.drawingAreaSquareKilometers > constants.MAX_GEOMETRY_SIZE
  }

  @computed get getColor () {
    const item = dataStore.markerColors.find(item => item.id === this.color_id)

    return item ? item.active_color : defaultPolygonColor
  }

  documentKeyup (e) {
    if (e.key && e.key === 'Escape' && this.drawing) {
      return this.points.clear()
    }
  }

  documentKeydown (e) {
    if (e.key) {
      const key = e.key.toLowerCase()
      if ((e.ctrlKey || e.metaKey) && key === 'z') {
        this.undoLastPoint()
      }
    }
  }

  undoLastPoint () {
    if (!this.drawing) {
      this.drawing = true
      return
    }
    this.points.pop()
    this.removing = false
  }

  documentDblclick (e) {
    if (this.drawing) {
      e.stopPropagation()
      if (this.points.length > 1) {
        this.drawing = false
      }
    }
  }

  mapMouseup (e) {
    if (!this.drawing) {
      return
    }
    if (this.removing) {
      this.undoLastPoint()
    } else {
      this.addPoint()
    }
  }

  mapMousemove (e) {
    this.position.latLng = e.latlng
    const lastPoint = this.points[this.points.length - 1]
    if (lastPoint) {
      const lastPointLayer = e.target.latLngToLayerPoint(
        turfPointToLatLng(lastPoint)
      )
      const pixelDistance = e.layerPoint.distanceTo(lastPointLayer)
      this.removing = pixelDistance < REMOVING_DISTANCE
    }
  }

  addPoint () {
    if (this.points.length < 2 || !_.isEqual(
      this.position.geoJSON, this.points[this.points.length - 1]
    )) {
      this.points.push(this.position.geoJSON)
    }
  }

  get selection () {
    return {
      item: this,
      type: selectionTypes.LINE,
      data: { focusedInteraction: true }
    }
  }

  @catchError
  async updateShape () {
    const wkt = new Wkt.Wkt()
    wkt.read(JSON.stringify(this.region))
    const result = await api.patchGeopolygon(this.id, {
      name: this.name,
      the_geom: wkt.write(),
      color: this.color_id,
      color_transparency: this.transparency,
      buffer: {
        radius: this.buffer,
        unit: this.bufferUnit,
        points: toJS(this.points)
      }
    })
    return result.data
  }

  validate (targetGroup) {
    const group = targetGroup || (selectionStore.data && selectionStore.data.group) || (dataStore.targetGroups && dataStore.targetGroups[0])
    const geopolygons = group && group.geopolygons ? group.geopolygons : []
    if (!this.isUpdating && this.name && _.find(geopolygons, { name: this.name.toString() })) {
      this.invalid.set('name', i18next.t('GLOBAL.DUPLICATE_ELEMENT_NAME', { element: i18next.t('GLOBAL.GEOPOLYGON') }))
      return false
    }

    this.invalid.clear()
    if (this.name === null || _.isEmpty(this.name.toString())) {
      this.invalid.set('name', i18next.t('INVALID.REQUIRED'))
    }

    if (this.buffer < 1) {
      this.invalid.set('buffer', i18next.t('INVALID.AT_LEAST_1'))
    }

    return this.invalid.size === 0
  }

  @catchError
  async add (targetGroupId) {
    if (this.validate()) {
      const wkt = new Wkt.Wkt()
      wkt.read(JSON.stringify(this.region))
      const effectiveColorId = this.color_id != null ? this.color_id : (getDefaultGrayColor(dataStore && dataStore.markerColors)?.id ?? null)
      const result = await api.postGeopolygon({
        name: this.name,
        the_geom: wkt.write(),
        target_group: targetGroupId,
        type: shapeTypes.LINE_BUFFER_SHAPE,
        color: effectiveColorId,
        color_transparency: this.transparency,
        buffer: {
          radius: this.buffer,
          unit: this.bufferUnit,
          points: toJS(this.points)
        }
      })
      return result.data
    }
  }

  /** Add line buffer to draft mode instead of saving to backend immediately */
  async addToDraft (poi) {
    if (!this.validate()) {
      return false
    }

    if (!this.region) {
      this.invalid.set('name', i18next.t('ITEM.DRAW_LINE_REQUIRED') || 'Please draw a line first')
      return false
    }

    const effectiveColorId = this.color_id != null ? this.color_id : (getDefaultGrayColor(dataStore && dataStore.markerColors)?.id ?? null)

    // Store the polygon data in the POI draft state (this will trigger POI search)
    await poi.setDraftPolygon({
      name: this.name,
      geometry: this.region,
      color_id: effectiveColorId,
      transparency: this.transparency,
      type: 'lineBuffer',
      buffer: this.buffer,
      points: toJS(this.points)
    })

    return true
  }
}
export default Line
